import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { usePwaInstall } from "../hooks/usePwaInstall";

/**
 * AIGL Install Prompt — branded bottom banner.
 * - Uses the shared `usePwaInstall` hook (singleton beforeinstallprompt listener).
 * - Hidden if already installed, or dismissed in the last 7 days.
 * - On iOS Safari (no native prompt), shows a Share → Add to Home Screen hint.
 *
 * The Header has a small "INSTALL" button that's always available on demand
 * even after this banner is dismissed.
 */
const DISMISS_KEY = "aigl-pwa-install-dismissed-at";
const DISMISS_DAYS = 7;

function wasRecentlyDismissed() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

export default function InstallPrompt() {
  const { canInstall, isInstalled, isIOS, install } = usePwaInstall();
  const [dismissed, setDismissed] = useState(false);
  const [iosVisible, setIosVisible] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (wasRecentlyDismissed()) setDismissed(true);
  }, []);

  // Slow iOS hint reveal (avoid slamming on first paint)
  useEffect(() => {
    if (isInstalled || dismissed) return;
    if (isIOS && !canInstall) {
      const t = setTimeout(() => setIosVisible(true), 5000);
      return () => clearTimeout(t);
    }
  }, [isIOS, canInstall, isInstalled, dismissed]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // noop
    }
    setDismissed(true);
    setIosVisible(false);
  };

  const handleInstall = async () => {
    setInstalling(true);
    try {
      const { outcome } = await install();
      if (outcome !== "accepted") dismiss();
    } finally {
      setInstalling(false);
    }
  };

  if (isInstalled || dismissed) return null;

  // iOS hint card
  if (iosVisible && !canInstall) {
    return (
      <div
        data-testid="pwa-ios-install-hint"
        className="fixed bottom-4 left-4 right-4 z-[100] mx-auto max-w-md rounded-lg border border-[#C5A059]/50 bg-[#0A192F] text-white shadow-2xl"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.5rem)" }}
      >
        <div className="flex items-start gap-3 p-4">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-[#C5A059]/15 text-[#C5A059]">
            <Download className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold tracking-[0.25em] text-[#C5A059]">
              INSTALL AIGL
            </div>
            <p className="mt-1 text-[13px] leading-snug text-white/90">
              Tap <span className="font-semibold">Share</span> in Safari, then{" "}
              <span className="font-semibold">Add to Home Screen</span> for a 1-tap launch.
            </p>
          </div>
          <button
            type="button"
            data-testid="pwa-ios-install-dismiss"
            onClick={dismiss}
            aria-label="Dismiss install hint"
            className="ml-1 -mr-1 -mt-1 flex h-8 w-8 items-center justify-center rounded text-white/60 hover:bg-white/10 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  if (!canInstall) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 z-[100] mx-auto max-w-md rounded-lg border border-[#C5A059]/50 bg-[#0A192F] text-white shadow-2xl"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.5rem)" }}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-[#C5A059]/15 text-[#C5A059]">
          <Download className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold tracking-[0.25em] text-[#C5A059]">
            INSTALL AIGL
          </div>
          <p className="mt-1 text-[13px] leading-snug text-white/90">
            Get instant access — install AIGL Career Launch to your home screen.
          </p>
        </div>
        <button
          type="button"
          data-testid="pwa-install-dismiss"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="ml-1 -mr-1 -mt-1 flex h-8 w-8 items-center justify-center rounded text-white/60 hover:bg-white/10 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex items-center justify-end gap-2 px-4 pb-4">
        <button
          type="button"
          data-testid="pwa-install-later"
          onClick={dismiss}
          className="text-xs font-semibold tracking-[0.15em] text-white/70 hover:text-white"
        >
          NOT NOW
        </button>
        <button
          type="button"
          data-testid="pwa-install-accept"
          onClick={handleInstall}
          disabled={installing}
          className="rounded-sm bg-[#C5A059] px-4 py-2 text-xs font-bold tracking-[0.15em] text-[#0A192F] transition hover:bg-[#d4b070] disabled:opacity-60"
        >
          {installing ? "INSTALLING…" : "INSTALL APP"}
        </button>
      </div>
    </div>
  );
}
